import React, { useEffect, useState } from "react";
import {
  fetchMenu,
  fetchTopSellers,
  getCurrency,
  resolveImageUrl,
  setChefPick,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    title: "Recommendations",
    intro:
      'What guests see at the top of their menu, as a "Recommended" strip. Chef picks come first; if there\'s room, auto top-sellers fill the rest (max 6 items total).',
    loading: "Loading…",
    errLoad: "Could not load recommendations.",
    errUpdate: "Could not update chef pick.",
    errCaption: "Could not save caption.",
    promptCaption:
      "Caption shown under this item in the Recommended strip (leave blank to clear):",
    chefPicksTitle: "⭐ Current chef picks ({n})",
    chefPicksHelp:
      "Pinned to the top of the customer menu in the order shown (oldest pick first). Up to 6 items appear in total — the rest of the strip auto-fills from top sellers.",
    noChefPicksPre: 'No chef picks yet. Use the "Top sellers" panel on the right to ⭐ items, or set chef picks directly from the full menu editor at ',
    noChefPicksPost: ".",
    noCaption: "(no caption)",
    editCaption: "Edit caption",
    remove: "Remove",
    topSellersTitle: "📊 Top sellers (last {n} days)",
    topSellersHelp:
      "Ranked by units sold. Items already flagged as chef picks show a filled star; click to toggle.",
    noOrders:
      "No orders in the last {n} days yet. As soon as guests start ordering, the top sellers will appear here and you can ⭐ them with one click.",
    sold: "sold",
    makePick: "Make chef pick",
    removePick: "Remove chef pick",
    howTitle: "How the Recommended strip works",
    how1Bold: "Chef picks come first",
    how1Rest: ", in the order they were flagged.",
    how2Bold: "Auto top-sellers fill the rest",
    how2Rest:
      ", up to a total of 6 items in the customer-facing strip. (Items already picked by the chef are not duplicated.)",
    how3Bold: "Unavailable items are excluded",
    how3Rest:
      ' — toggle "Available" off on the menu editor and an item disappears from recommendations.',
    how4Bold: "The strip hides itself",
    how4Rest:
      " when there are no chef picks AND no orders yet (brand-new install), so guests aren't shown an empty section.",
  },
  am: {
    title: "ምክሮች",
    intro:
      'እንግዶች በሜኑአቸው ላይኛው ክፍል እንደ "የተመከረ" ሰቅ የሚያዩት። የሼፍ ምርጫዎች መጀመሪያ ይመጣሉ፤ ቦታ ካለ፣ ራስ-ሰር ከፍተኛ ሻጮች ቀሪውን ይሞላሉ (ከ6 ዕቃዎች በላይ አይደለም)።',
    loading: "በመጫን ላይ…",
    errLoad: "ምክሮችን መጫን አልተቻለም።",
    errUpdate: "የሼፍ ምርጫን ማዘመን አልተቻለም።",
    errCaption: "መግለጫ ጽሑፉን ማስቀመጥ አልተቻለም።",
    promptCaption:
      "በተመከረ ሰቅ ውስጥ ከዚህ ዕቃ ስር የሚታየው መግለጫ (ለማጽዳት ባዶ ይተዉት):",
    chefPicksTitle: "⭐ የአሁኑ የሼፍ ምርጫዎች ({n})",
    chefPicksHelp:
      "በደንበኛ ሜኑ ላይኛው ክፍል በሚታየው ቅደም ተከተል ተሰክተዋል (የቆየ ምርጫ መጀመሪያ)። በድምሩ እስከ 6 ዕቃዎች ይታያሉ — ቀሪው ሰቅ ከከፍተኛ ሻጮች በራስ-ሰር ይሞላል።",
    noChefPicksPre: 'እስካሁን የሼፍ ምርጫ የለም። ዕቃዎችን ⭐ ለማድረግ በቀኝ በኩል ያለውን "ከፍተኛ ሻጮች" ፓነል ይጠቀሙ፣ ወይም የሼፍ ምርጫዎችን በቀጥታ ከሙሉ ሜኑ አርታኢ ',
    noChefPicksPost: " ላይ ያዘጋጁ።",
    noCaption: "(መግለጫ የለም)",
    editCaption: "መግለጫ አርትዕ",
    remove: "አጥፋ",
    topSellersTitle: "📊 ከፍተኛ ሻጮች (ያለፉት {n} ቀናት)",
    topSellersHelp:
      "በተሸጡ ብዛት የተደረደሩ። እንደ ሼፍ ምርጫ የተመለከቱ ዕቃዎች የተሞላ ኮከብ ያሳያሉ፤ ለመቀየር ይጫኑ።",
    noOrders:
      "ባለፉት {n} ቀናት ውስጥ እስካሁን ምንም ትዕዛዝ የለም። እንግዶች ማዘዝ እንደጀመሩ፣ ከፍተኛ ሻጮች እዚህ ይታያሉ እና በአንድ ጠቅታ ⭐ ማድረግ ይችላሉ።",
    sold: "ተሸጧል",
    makePick: "የሼፍ ምርጫ አድርግ",
    removePick: "የሼፍ ምርጫ አስወግድ",
    howTitle: "የተመከረ ሰቅ እንዴት እንደሚሰራ",
    how1Bold: "የሼፍ ምርጫዎች መጀመሪያ ይመጣሉ",
    how1Rest: "፣ በተመለከቱበት ቅደም ተከተል።",
    how2Bold: "ራስ-ሰር ከፍተኛ ሻጮች ቀሪውን ይሞላሉ",
    how2Rest:
      "፣ በደንበኛ-ፊት ሰቅ ውስጥ እስከ ድምር 6 ዕቃዎች ድረስ። (በሼፍ አስቀድሞ የተመረጡ ዕቃዎች አይደገሙም።)",
    how3Bold: "የማይገኙ ዕቃዎች ይገለላሉ",
    how3Rest:
      ' — በሜኑ አርታኢ ላይ "ይገኛል"ን ያጥፉ እና ዕቃው ከምክሮች ይጠፋል።',
    how4Bold: "ሰቁ ራሱን ይደብቃል",
    how4Rest:
      " ምንም የሼፍ ምርጫ እና ምንም ትዕዛዝ ሳይኖር (አዲስ ጭነት)፣ ስለዚህ እንግዶች ባዶ ክፍል አይታዩም።",
  },
  om: {
    title: "Yaada",
    intro:
      'Wanti keessummoonni gubbaa meenuu isaanii irratti akka sarara "Yaadame"tti argan. Filannoon Cheef dura dhufa; yoo bakki jiraate, gurguraan ol\'aanaa ofumaan hafan guuta (waliigalatti meeshaa 6 hin caalu).',
    loading: "Fe'aat jira…",
    errLoad: "Yaada fe'uun hin danda'amne.",
    errUpdate: "Filannoo Cheef haaromsuun hin danda'amne.",
    errCaption: "Ibsa olkaa'uun hin danda'amne.",
    promptCaption:
      "Ibsa sarara Yaadame keessatti meeshaa kana jala mul'atu (haquuf duwwaa dhiisi):",
    chefPicksTitle: "⭐ Filannoo Cheef Ammaa ({n})",
    chefPicksHelp:
      "Tartiiba mul'atuun gubbaa meenuu maamilaa irratti maxxanfame (filannoo durii dura). Waliigalatti hanga meeshaa 6 mul'ata — sararri hafe gurguraa ol'aanaa irraa ofumaan guutama.",
    noChefPicksPre: 'Hanga ammaatti filannoon Cheef hin jiru. Meeshaa ⭐ gochuuf paanaalii "Gurguraa ol\'aanaa" mirga irra jiru fayyadami, yookiin filannoo Cheef kallattiidhaan gulaalaa meenuu guutuu ',
    noChefPicksPost: " irraa qindeessi.",
    noCaption: "(ibsa hin jiru)",
    editCaption: "Ibsa Sirreessi",
    remove: "Balleessi",
    topSellersTitle: "📊 Gurguraa ol'aanaa (guyyoota {n} darban)",
    topSellersHelp:
      "Baay'ina gurgurameen tartiibeffame. Meeshaaleen duraan akka filannoo Cheef mallatteeffaman urjii guutame agarsiisu; jijjiiruuf cuqaasi.",
    noOrders:
      "Guyyoota {n} darban keessatti hanga ammaatti ajajni hin jiru. Akkuma keessummoonni ajajuu jalqaban, gurguraan ol'aanaa as ni mul'ata, cuqaasaa tokkoon ⭐ gochuu dandeessa.",
    sold: "gurgurame",
    makePick: "Filannoo Cheef godhi",
    removePick: "Filannoo Cheef haqi",
    howTitle: "Sararri Yaadame akkamitti hojjeta",
    how1Bold: "Filannoon Cheef dura dhufa",
    how1Rest: ", tartiiba itti mallatteeffamaniin.",
    how2Bold: "Gurguraan ol'aanaa ofumaan hafe guuta",
    how2Rest:
      ", sarara maamilaa fuulduratti waliigalatti hanga meeshaa 6tti. (Meeshaaleen duraan Cheefiin filataman hin irra deebi'aman.)",
    how3Bold: "Meeshaaleen hin argamne ni baafamu",
    how3Rest:
      ' — gulaalaa meenuu irratti "Ni argama" dhaamsi, meeshichis yaada irraa bada.',
    how4Bold: "Sararri ofii isaa dhokfata",
    how4Rest:
      " yeroo filannoon Cheef hin jirre FI ajajni hanga ammaatti hin jirre (sirna haaraa), kanaaf keessummoonni kutaa duwwaa hin agarsiifaman.",
  },
};

// -----------------------------------------------------------------------
// RecommendationsPage — chef + manager curation surface.
//
// Two panels side by side:
//   1. "Top sellers (last 30 days)" — read-only insight. Sorted by
//      units sold; each row has a quick toggle to flip the item into a
//      chef's pick (or remove the pick).
//   2. "Currently flagged as chef picks" — what guests see right now at
//      the top of their menu. Quick-remove from here.
//
// Customer-facing logic: chef picks come first in the Recommended strip,
// then auto top-sellers fill remaining slots up to 6 total. So if the
// chef flags zero items, customers still see the top sellers; if the
// chef flags 6+, customers see only the chef's curation.
// -----------------------------------------------------------------------

export default function RecommendationsPage() {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  const currency = getCurrency();
  const [topSellers, setTopSellers] = useState([]);
  const [windowDays, setWindowDays] = useState(30);
  const [chefPicks, setChefPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [stats, allMenu] = await Promise.all([
        fetchTopSellers(),
        fetchMenu(),
      ]);
      setTopSellers(Array.isArray(stats.items) ? stats.items : []);
      setWindowDays(stats.window_days || 30);
      setChefPicks(
        (Array.isArray(allMenu) ? allMenu : []).filter((m) => m.is_chef_pick),
      );
    } catch (e) {
      setErr(e?.response?.data?.detail || t("errLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePick = async (menuId, currentlyPick, note = null) => {
    setSavingId(menuId);
    try {
      await setChefPick(menuId, !currentlyPick, note);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || t("errUpdate"));
    } finally {
      setSavingId(null);
    }
  };

  // Quick-edit caption for an already-picked item — opens a tiny prompt
  // rather than a modal to keep this page simple. (For more complex
  // editing the chef can use the full menu editor on /menu.)
  const editNote = async (item) => {
    const next = window.prompt(
      t("promptCaption"),
      item.chef_pick_note || "",
    );
    if (next === null) return;  // user cancelled
    setSavingId(item.id);
    try {
      await setChefPick(item.id, true, next.trim() || null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || t("errCaption"));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <h1>{t("title")}</h1>
      <p className="muted">{t("intro")}</p>

      {err && <div className="error">{err}</div>}
      {loading && <p className="muted">{t("loading")}</p>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginTop: 12,
        }}
      >
        {/* ============ CHEF PICKS (current state) ============ */}
        <div className="card">
          <h3 style={{ marginTop: 0 }}>
            {t("chefPicksTitle").replace("{n}", chefPicks.length)}
          </h3>
          <p className="muted" style={{ marginTop: 0, fontSize: ".88rem" }}>
            {t("chefPicksHelp")}
          </p>
          {chefPicks.length === 0 ? (
            <div
              style={{
                background: "#fffaf0", border: "1px dashed #f0d38b",
                borderRadius: 8, padding: "14px 16px", color: "#6c5a00",
                fontSize: ".92rem",
              }}
            >
              {t("noChefPicksPre")}<code>/menu</code>{t("noChefPicksPost")}
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {chefPicks.map((item) => (
                <li
                  key={item.id}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "10px 0",
                    borderBottom: "1px solid #f0ece6",
                  }}
                >
                  {item.image_url ? (
                    <img
                      src={resolveImageUrl(item.image_url)}
                      alt=""
                      style={{
                        width: 48, height: 48, objectFit: "cover",
                        borderRadius: 6, flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 48, height: 48, borderRadius: 6,
                      background: "#f0ece6", flexShrink: 0,
                    }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      <span style={{ color: "#a8892a" }}>⭐</span> {item.name}
                    </div>
                    {item.chef_pick_note ? (
                      <div className="muted" style={{ fontSize: ".82rem", fontStyle: "italic" }}>
                        "{item.chef_pick_note}"
                      </div>
                    ) : (
                      <div className="muted" style={{ fontSize: ".82rem" }}>
                        {t("noCaption")}
                      </div>
                    )}
                    <div className="muted" style={{ fontSize: ".78rem" }}>
                      {item.category} · {formatPrice(item.price, currency)}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button
                      type="button"
                      className="btn secondary small"
                      disabled={savingId === item.id}
                      onClick={() => editNote(item)}
                    >
                      {t("editCaption")}
                    </button>
                    <button
                      type="button"
                      className="btn danger small"
                      disabled={savingId === item.id}
                      onClick={() => togglePick(item.id, true)}
                    >
                      {savingId === item.id ? "…" : t("remove")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ============ TOP SELLERS (auto insight) ============ */}
        <div className="card">
          <h3 style={{ marginTop: 0 }}>
            {t("topSellersTitle").replace("{n}", windowDays)}
          </h3>
          <p className="muted" style={{ marginTop: 0, fontSize: ".88rem" }}>
            {t("topSellersHelp")}
          </p>
          {topSellers.length === 0 ? (
            <div
              style={{
                background: "#e9eef0", border: "1px dashed #c7d2d6",
                borderRadius: 8, padding: "14px 16px", color: "#3e4c59",
                fontSize: ".92rem",
              }}
            >
              {t("noOrders").replace("{n}", windowDays)}
            </div>
          ) : (
            <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {topSellers.map((item, idx) => (
                <li
                  key={item.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 0",
                    borderBottom: "1px solid #f0ece6",
                  }}
                >
                  <div
                    style={{
                      width: 22, textAlign: "right", fontVariantNumeric: "tabular-nums",
                      color: idx < 3 ? "#a8892a" : "#5c7c7f",
                      fontWeight: idx < 3 ? 700 : 500,
                    }}
                  >
                    {idx + 1}
                  </div>
                  {item.image_url ? (
                    <img
                      src={resolveImageUrl(item.image_url)}
                      alt=""
                      style={{
                        width: 40, height: 40, objectFit: "cover",
                        borderRadius: 6, flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 40, height: 40, borderRadius: 6,
                      background: "#f0ece6", flexShrink: 0,
                    }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    <div className="muted" style={{ fontSize: ".78rem" }}>
                      {item.category} · {formatPrice(item.price, currency)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", marginRight: 8 }}>
                    <div style={{ fontWeight: 700 }}>{item.units_sold_30d}</div>
                    <div className="muted" style={{ fontSize: ".7rem" }}>{t("sold")}</div>
                  </div>
                  <button
                    type="button"
                    className="btn small"
                    disabled={savingId === item.id}
                    title={item.is_chef_pick ? t("removePick") : t("makePick")}
                    onClick={() => togglePick(item.id, item.is_chef_pick)}
                    style={{
                      background: item.is_chef_pick ? "#d4af37" : "#fff",
                      border: "1px solid " + (item.is_chef_pick ? "#a8892a" : "#cbd2d9"),
                      color: item.is_chef_pick ? "#fff" : "#3e4c59",
                      minWidth: 38,
                    }}
                  >
                    {savingId === item.id ? "…" : item.is_chef_pick ? "★" : "☆"}
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, background: "#fbf9f4" }}>
        <h3 style={{ marginTop: 0 }}>{t("howTitle")}</h3>
        <ul style={{ marginBottom: 0, lineHeight: 1.7 }}>
          <li>
            <b>{t("how1Bold")}</b>{t("how1Rest")}
          </li>
          <li>
            <b>{t("how2Bold")}</b>{t("how2Rest")}
          </li>
          <li>
            <b>{t("how3Bold")}</b>{t("how3Rest")}
          </li>
          <li>
            <b>{t("how4Bold")}</b>{t("how4Rest")}
          </li>
        </ul>
      </div>
    </>
  );
}
